import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "cvi_cookie_consent";

const loadConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

export const CookieConsent = () => {
  const [consent, setConsent] = useState(undefined);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [personalisation, setPersonalisation] = useState(false);

  useEffect(() => {
    setConsent(loadConsent());
  }, []);

  const save = (value) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, ts: new Date().toISOString() }));
    setConsent(value);
    setShowPrefs(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, personalisation: true });
  const rejectAll = () => save({ necessary: true, analytics: false, personalisation: false });
  const savePrefs = () => save({ necessary: true, analytics, personalisation });

  if (consent === undefined || consent !== null) return null;

  return (
    <>
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 1.2 }}
        className="fixed bottom-0 inset-x-0 z-[90] bg-navy/95 backdrop-blur-xl border-t border-gold/30"
        data-testid="cookie-banner"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col lg:flex-row lg:items-center gap-5 justify-between">
          <div className="max-w-2xl">
            <p className="font-serif text-lg font-semibold text-ivory mb-1.5">We value your privacy</p>
            <p className="text-xs text-ivory/60 leading-relaxed">
              Not all cookies (or biscuits) are unhealthy. We use cookies to understand how
              you're using our site, which helps us improve and personalise things for you.
              Please note that visiting our site does not make you a CVI patient. Read our{" "}
              <Link to="/privacy" data-testid="cookie-privacy-link" className="text-gold hover:text-champagne underline underline-offset-2">
                privacy notice
              </Link>{" "}
              and{" "}
              <Link to="/cookies" data-testid="cookie-policy-link" className="text-gold hover:text-champagne underline underline-offset-2">
                cookie policy
              </Link>
              .
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <button
              onClick={rejectAll}
              data-testid="cookie-reject-button"
              className="font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3 border border-ivory/30 text-ivory hover:border-gold hover:text-gold transition-colors"
            >
              Reject
            </button>
            <button
              onClick={() => setShowPrefs(true)}
              data-testid="cookie-preferences-button"
              className="font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3 border border-ivory/30 text-ivory hover:border-gold hover:text-gold transition-colors"
            >
              Cookie Preferences
            </button>
            <button
              onClick={acceptAll}
              data-testid="cookie-accept-button"
              className="font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3 bg-gold text-navy hover:bg-champagne transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showPrefs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setShowPrefs(false)}
            data-testid="cookie-preferences-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="bg-ivory max-w-lg w-full p-8 lg:p-10 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowPrefs(false)}
                data-testid="cookie-preferences-close"
                className="absolute top-5 right-5 text-navy/40 hover:text-navy transition-colors"
                aria-label="Close preferences"
              >
                <X size={18} />
              </button>
              <h2 className="font-serif text-2xl font-semibold text-navy mb-6">Cookie preferences</h2>

              <div className="space-y-5">
                <div className="flex items-start justify-between gap-6 border-b border-navy/10 pb-5">
                  <div>
                    <p className="text-sm font-semibold text-navy">Strictly necessary</p>
                    <p className="text-xs text-navy/50 mt-1 leading-relaxed">
                      Required for sign-in and core site functions. Always on.
                    </p>
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-gold shrink-0 pt-1">
                    Always On
                  </span>
                </div>

                {[
                  ["Analytics", "Helps us understand how the site is used so we can improve it.", analytics, setAnalytics, "cookie-toggle-analytics"],
                  ["Personalisation", "Remembers your preferences for a more tailored experience.", personalisation, setPersonalisation, "cookie-toggle-personalisation"],
                ].map(([label, desc, value, setter, testid]) => (
                  <div key={label} className="flex items-start justify-between gap-6 border-b border-navy/10 pb-5">
                    <div>
                      <p className="text-sm font-semibold text-navy">{label}</p>
                      <p className="text-xs text-navy/50 mt-1 leading-relaxed">{desc}</p>
                    </div>
                    <button
                      role="switch"
                      aria-checked={value}
                      onClick={() => setter(!value)}
                      data-testid={testid}
                      className={`w-11 h-6 shrink-0 mt-1 rounded-full transition-colors relative ${value ? "bg-gold" : "bg-navy/20"}`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 bg-white shadow transition-[left] ${value ? "left-[22px]" : "left-0.5"}`}
                      />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={savePrefs}
                data-testid="cookie-save-preferences-button"
                className="mt-8 w-full bg-navy text-ivory font-mono text-[11px] tracking-[0.25em] uppercase py-4 hover:bg-gold hover:text-navy transition-colors"
              >
                Save Preferences
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
